import type { AudioAsset, Take } from '@tekvoice/shared';

/**
 * SPEC §4 — every rendered URL carries `?v={generation}` as cache-bust.
 * The API already appends it where it knows the generation; this only fills the
 * gap so a URL never renders without one when a generation is available.
 */
export function withVersion(url: string, generation: string | null): string {
  if (!generation) return url;
  if (/[?&]v=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(generation)}`;
}

export interface PlayableAudio {
  url: string;
  /** `published` = the approved object in the batch folder, `staging` = the current take. */
  source: 'published' | 'staging';
  durationMs: number | null;
}

/**
 * What the audio cell plays: the published (delivered) object once approved,
 * otherwise the current take's staging object (SPEC §4).
 */
export function assetAudio(asset: AudioAsset | null): PlayableAudio | null {
  if (!asset) return null;
  if (asset.status === 'APPROVED' && asset.publishedUrl) {
    return {
      url: withVersion(asset.publishedUrl, asset.publishedGeneration),
      source: 'published',
      durationMs: asset.currentTake?.durationMs ?? null,
    };
  }
  return takeAudio(asset.currentTake);
}

/**
 * TODO-CONTRACT: `take.url` is nullable and the client has no public-URL prefix
 * of its own (only `GET /api/settings/status` exposes one, and EMPLOYEEs may not
 * see it), so a take without a URL simply cannot be played — the cell disables
 * play instead of guessing a path from `stagingPath`.
 */
export function takeAudio(take: Take | null): PlayableAudio | null {
  if (!take?.url) return null;
  return { url: take.url, source: 'staging', durationMs: take.durationMs };
}

export function formatDuration(durationMs: number | null): string {
  if (durationMs === null || Number.isNaN(durationMs)) return '—';
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Browser download of an exported CSV blob (SPEC §7). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
